import { UserManager, WebStorageStateStore } from "oidc-client";
import { oidcConfig } from "./oidcConfig";

const store = new WebStorageStateStore({ store: window.localStorage });
// (After it works you can move back to sessionStorage if you want)

export const userManager = new UserManager({
    ...oidcConfig,
    userStore: store,
    stateStore: store, // IMPORTANT for "No matching state found"
    filterProtocolClaims: true,
});

let refreshInFlight: Promise<string | null> | null = null;
let callbackInFlight: Promise<string> | null = null;

const getAuthCodeKey = (): string | null => {
    try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        if (!code) {
            return null;
        }
        return `oidc.code.consumed:${code}:${state ?? ''}`;
    } catch {
        return null;
    }
};

async function refreshAccessTokenLocked(): Promise<string | null> {
    if (refreshInFlight) {
        return refreshInFlight;
    }

    refreshInFlight = (async () => {
        try {
            const refreshedUser = await userManager.signinSilent();
            return refreshedUser?.access_token ?? null;
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            if (message.toLowerCase().includes('already been consumed')) {
                const latestUser = await userManager.getUser();
                if (latestUser && !latestUser.expired && latestUser.access_token) {
                    return latestUser.access_token;
                }
            }

            console.error('Silent refresh failed', error);
            return null;
        } finally {
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
}

export async function startLogin(returnTo: string) {
    await userManager.signinRedirect({
        state: { returnTo },
    });
}

export async function handleCallback(): Promise<string> {
    if (callbackInFlight) {
        return callbackInFlight;
    }

    callbackInFlight = (async () => {
        const authCodeKey = getAuthCodeKey();
        if (authCodeKey && window.sessionStorage.getItem(authCodeKey) === '1') {
            return "/";
        }

        const user = await userManager.signinRedirectCallback();
        if (authCodeKey) {
            window.sessionStorage.setItem(authCodeKey, '1');
        }
        return (user.state as any)?.returnTo || "/";
    })();

    try {
        return await callbackInFlight;
    } finally {
        callbackInFlight = null;
    }
}

export async function isLoggedIn(): Promise<boolean> {
    const user = await userManager.getUser();
    return !!user?.access_token && !user.expired;
}

export async function getAccessToken(): Promise<string | null> {
    const user = await userManager.getUser();
    if (user && !user.expired) {
        return user.access_token ?? null;
    }

    return refreshAccessTokenLocked();
}

export async function forceRefreshAccessToken(): Promise<string | null> {
    return refreshAccessTokenLocked();
}

export async function getTokenDiagnostics() {
    const user = await userManager.getUser();
    return {
        isAuthenticated: !!user,
        isExpired: !!user?.expired,
        hasAccessToken: !!user?.access_token,
        hasRefreshToken: !!user?.refresh_token,
        scope: user?.scope ?? null,
        tokenType: user?.token_type ?? null,
    };
}

